import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Solution } from '../../types/solution';
import { FilterOption } from '../../types/common';
import { SolutionService } from '../../services/solutionService';

interface SolutionsState {
  solutions: Solution[];
  filterOptions: FilterOption[];
  activeFilters: string[];
  searchQuery: string;
  viewMode: 'grid' | 'list';
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

// Load persisted state from localStorage
const loadPersistedState = () => {
  try {
    const activeFilters = localStorage.getItem('hcltech_solutions_filters');
    const viewMode = localStorage.getItem('hcltech_solutions_view_mode');
    const searchQuery = localStorage.getItem('hcltech_solutions_search');
    
    return {
      activeFilters: activeFilters ? JSON.parse(activeFilters) : [],
      viewMode: (viewMode as 'grid' | 'list') || 'grid',
      searchQuery: searchQuery || '',
    };
  } catch (error) {
    return {
      activeFilters: [],
      viewMode: 'grid' as const,
      searchQuery: '',
    };
  }
};

const persistedState = loadPersistedState();

const initialState: SolutionsState = {
  solutions: [],
  filterOptions: [],
  activeFilters: persistedState.activeFilters,
  searchQuery: persistedState.searchQuery,
  viewMode: persistedState.viewMode,
  loading: false,
  error: null,
  lastFetched: null,
};

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

// Async thunk to fetch all solutions
export const fetchAllSolutions = createAsyncThunk(
  'solutions/fetchAll',
  async (_, { getState }) => {
    const state = getState() as { solutions: SolutionsState };
    const now = Date.now();
    
    // Check if cache is still valid
    if (
      state.solutions.lastFetched &&
      now - state.solutions.lastFetched < CACHE_DURATION &&
      state.solutions.solutions.length > 0
    ) {
      // Return cached data
      return {
        solutions: state.solutions.solutions,
        filterOptions: state.solutions.filterOptions,
      };
    }
    
    // Fetch fresh data
    const solutions = await SolutionService.getAllSolutions();
    const filterOptions = SolutionService.getFilterOptions(solutions);
    
    return { solutions, filterOptions };
  }
);

const solutionsSlice = createSlice({
  name: 'solutions',
  initialState,
  reducers: {
    toggleFilter: (state, action: PayloadAction<string>) => {
      const filterId = action.payload;
      if (state.activeFilters.includes(filterId)) {
        state.activeFilters = state.activeFilters.filter(id => id !== filterId);
      } else {
        state.activeFilters.push(filterId);
      }
    },
    clearFilters: (state) => {
      state.activeFilters = [];
      state.searchQuery = '';
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.viewMode = action.payload;
    },
    clearSolutionsCache: (state) => {
      state.solutions = [];
      state.filterOptions = [];
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllSolutions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllSolutions.fulfilled, (state, action) => {
        state.loading = false;
        state.solutions = action.payload.solutions;
        state.filterOptions = action.payload.filterOptions;
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(fetchAllSolutions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load solutions';
      });
  },
});

export const {
  toggleFilter,
  clearFilters,
  setSearchQuery,
  setViewMode,
  clearSolutionsCache,
} = solutionsSlice.actions;

export default solutionsSlice.reducer;
